import React, { useState } from 'react';
import type { FC } from 'react';
import { useDevCenterOverridesClient } from '@wix/dev-center-overrides-client';
import isString from 'lodash/isString.js';
import { parse } from 'semver';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { Box, Text, Alert, Spinner, useAsyncCallback } from '@wix/cli-ui-kit';
import { match } from 'variant';
import type { PreviewResult } from '../../PreviewCommand/index.js';
import { CreatePreview } from '../Steps/CreatePreview.js';
import { useProjectModel } from '../../../model.js';
import { ReleaseVersionType } from './types.js';
import { Warnings } from './Warnings.js';
import { ReleaseComment } from './ReleaseComment.js';
import { ConfirmMinorVersion } from './ConfirmMinorVersion.js';
import { ChooseVersion } from './ChooseVersion.js';
import { NextSteps } from './NextSteps.js';

interface ReleaseProps {
  baseUrl?: string;
  label?: string;
  preEnteredComment?: string;
  preSelectedVersionType?: ReleaseVersionType;
}

export const ReleaseApp: FC<ReleaseProps> = ({
  baseUrl,
  label,
  preEnteredComment,
  preSelectedVersionType,
}) => {
  const {
    model: {
      config: { appId },
    },
  } = useProjectModel();
  const { createDraftVersion, releaseDraftVersion } =
    useDevCenterOverridesClient();

  const [preview, setPreview] = useState<PreviewResult>();
  const [comment, setComment] = useState<string | undefined>();
  const [versionType, setVersionType] = useState<ReleaseVersionType>();
  const [userShouldSelectVersion, setUserShouldSelectVersion] = useState(false);

  const createDraftAction = useAsyncCallback(async (_, previewId: string) => {
    const { nextMajor, nextMinor } = await createDraftVersion(appId, previewId);
    const appWasNeverReleased = nextMajor === '1.0.0' && !nextMinor;

    const parsedNextMajor = parse(nextMajor);
    if (parsedNextMajor == null) {
      throw new CliError({
        code: CliErrorCode.FailedToParseNextVersion({ version: nextMajor }),
        cause: undefined,
      });
    }

    return {
      nextMinor,
      nextMajor,
      appWasNeverReleased,
      latestMajor: parsedNextMajor.major - 1,
    };
  });

  const releaseDraftAction = useAsyncCallback(
    async (
      _,
      versionType: ReleaseVersionType,
      comment: string,
      latestMajor: number
    ) => {
      const result = await releaseDraftVersion(appId, {
        createNewMajor: versionType === ReleaseVersionType.MAJOR,
        notes: comment,
        latestMajor,
      });

      return result;
    }
  );

  async function onReleaseCommentEntered({
    previewId,
    comment,
  }: {
    previewId: string;
    comment: string;
  }) {
    setComment(comment);

    const result = await createDraftAction.execute(previewId).catch(() => null);
    if (!result) {
      return;
    }

    const { appWasNeverReleased, nextMinor, latestMajor } = result;

    const chosenVersion = appWasNeverReleased
      ? ReleaseVersionType.MAJOR
      : preSelectedVersionType;

    if (
      preSelectedVersionType === ReleaseVersionType.MINOR &&
      (appWasNeverReleased || !nextMinor)
    ) {
      throw new CliError({
        cause: null,
        code: CliErrorCode.CannotReleaseMinorInNoninteractive(),
      });
    }

    if (chosenVersion) {
      void releaseDraftAction.execute(chosenVersion, comment, latestMajor);
    } else {
      setUserShouldSelectVersion(true);
    }
  }

  return (
    <>
      <CreatePreview
        baseUrl={baseUrl}
        label={label}
        onCreated={(result) => {
          setPreview(result);

          if (isString(preEnteredComment)) {
            void onReleaseCommentEntered({
              previewId: result.previewId,
              comment: preEnteredComment,
            });
          }
        }}
      />
      {preview && <Warnings components={preview.appManifest.components} />}
      {preview && !isString(preEnteredComment) && (
        <ReleaseComment
          onEntered={(comment) =>
            onReleaseCommentEntered({
              previewId: preview.previewId,
              comment,
            })
          }
        />
      )}
      {isString(comment) &&
        match(createDraftAction.status, {
          NotRequested: () => null,
          Error: () => null,
          Loading: () => <Spinner text="Updating draft application version" />,
          Success: ({ result: { nextMajor, nextMinor, latestMajor } }) => (
            <>
              {userShouldSelectVersion && (
                <ChooseVersion
                  nextMinor={nextMinor}
                  nextMajor={nextMajor}
                  onChosen={(selectedVersionType) => {
                    setVersionType(selectedVersionType);
                    if (selectedVersionType === ReleaseVersionType.MAJOR) {
                      void releaseDraftAction.execute(
                        selectedVersionType,
                        comment,
                        latestMajor
                      );
                    }
                  }}
                />
              )}
              {versionType === ReleaseVersionType.MINOR && (
                <ConfirmMinorVersion
                  onConfirmed={() =>
                    releaseDraftAction.execute(
                      versionType,
                      comment,
                      latestMajor
                    )
                  }
                />
              )}
            </>
          ),
        })}
      {match(releaseDraftAction.status, {
        NotRequested: () => null,
        Error: () => null,
        Loading: () => <Spinner text="Releasing..." />,
        Success: ({ result: { releasedVersion } }) => (
          <Box flexDirection="column" gap={1}>
            <Alert type="success">
              <Text bold>Version {releasedVersion} was created</Text>
            </Alert>
            <NextSteps />
          </Box>
        ),
      })}
    </>
  );
};
